import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    // Re-initialize the AbortController on every connection so listeners
    // registered below get a fresh signal. Without this, disconnect →
    // reconnect (theme editor morphs, section re-renders) leaves the
    // signal permanently aborted and the listeners never fire. Matches
    // the pattern used in quick-add.js and variant-picker.js.
    this.#abortController = new AbortController();

    super.connectedCallback();

    // Propagate --submenu-animation-speed from this element to body so the
    // fixed-position backdrop (body::after in blocks/_header-menu.liquid) can
    // fade at exactly the same speed as the submenu content. CSS custom
    // properties don't bubble up via :has(), so we copy it manually.
    const animSpeed = this.style.getPropertyValue('--submenu-animation-speed');
    if (animSpeed) document.body.style.setProperty('--submenu-animation-speed', animSpeed);

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Force-close the menu immediately on scroll. The mega menu panel is
    // position:absolute inside the header (which scrolls with the page),
    // so when the user scrolls, the panel visually disappears but the
    // fixed-position backdrop in blocks/_header-menu.liquid stays on
    // screen for DEACTIVATE_DELAY (350ms) until pointerleave fires.
    // Bypassing the debounce here keeps them in sync.
    window.addEventListener(
      'scroll',
      () => {
        if (!this.#state.activeItem) return;
        this.#debouncedActivateHandler.cancel();
        this.#debouncedDeactivate.cancel();
        this.#deactivate();
      },
      { signal: this.#abortController.signal, passive: true }
    );

    // Keyboard accessibility: handle Enter/Space/Escape/Arrow keys on menu items
    this.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle keyboard events for menu accessibility (SITE-139).
   * - Enter/Space: toggle submenu open/close
   * - ArrowDown: open submenu and focus first link
   * - Escape: close submenu and return focus to trigger
   * - ArrowUp/ArrowDown within submenu: navigate between links
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const { key } = event;
    const target = /** @type {HTMLElement} */ (event.target);

    // Check if we're on a top-level menu link with a submenu
    const menuLink = target.closest('[aria-haspopup="true"]');
    const isInSubmenu = target.closest('[ref="submenu[]"]');

    if (menuLink && !isInSubmenu) {
      // Top-level menu item with submenu
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        const listItem = menuLink.closest('.menu-list__list-item');
        if (!listItem) return;

        if (this.#state.activeItem === menuLink) {
          // Close if already open
          this.#debouncedActivateHandler.cancel();
          this.#debouncedDeactivate.cancel();
          this.#deactivate();
        } else {
          // Open the submenu
          this.activate({ target: listItem });
          // Focus first link in submenu after it opens
          requestAnimationFrame(() => {
            const submenu = findSubmenu(menuLink);
            if (submenu) {
              const firstLink = submenu.querySelector('a, button');
              if (firstLink instanceof HTMLElement) firstLink.focus();
            }
          });
        }
        return;
      }

      if (key === 'ArrowDown') {
        event.preventDefault();
        const listItem = menuLink.closest('.menu-list__list-item');
        if (!listItem) return;

        // Open submenu if not already open
        if (this.#state.activeItem !== menuLink) {
          this.activate({ target: listItem });
        }
        // Focus first link in submenu
        requestAnimationFrame(() => {
          const submenu = findSubmenu(menuLink);
          if (submenu) {
            const firstLink = submenu.querySelector('a, button');
            if (firstLink instanceof HTMLElement) firstLink.focus();
          }
        });
        return;
      }

      if (key === 'Escape') {
        event.preventDefault();
        this.#debouncedActivateHandler.cancel();
        this.#debouncedDeactivate.cancel();
        this.#deactivate();
        menuLink.focus();
        return;
      }
    }

    // Inside a submenu: arrow navigation and Escape
    if (isInSubmenu) {
      const focusableLinks = /** @type {HTMLElement[]} */ (
        [...isInSubmenu.querySelectorAll('a[href], button:not([disabled])')]
      );

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        const currentIndex = focusableLinks.indexOf(target);
        let nextIndex;
        if (key === 'ArrowDown') {
          nextIndex = currentIndex < focusableLinks.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableLinks.length - 1;
        }
        focusableLinks[nextIndex]?.focus();
        return;
      }

      if (key === 'Escape') {
        event.preventDefault();
        // Find the trigger link and close
        const triggerLink = this.#state.activeItem;
        this.#debouncedActivateHandler.cancel();
        this.#debouncedDeactivate.cancel();
        this.#deactivate();
        if (triggerLink instanceof HTMLElement) triggerLink.focus();
        return;
      }

      if (key === 'Tab') {
        // Allow natural tab but close menu when tabbing out of the last item
        const lastLink = focusableLinks[focusableLinks.length - 1];
        if (target === lastLink && !event.shiftKey) {
          this.#debouncedActivateHandler.cancel();
          this.#debouncedDeactivate.cancel();
          this.#deactivate();
        }
        // For shift+tab on first item, close and return to trigger
        const firstLink = focusableLinks[0];
        if (target === firstLink && event.shiftKey) {
          this.#debouncedActivateHandler.cancel();
          this.#debouncedDeactivate.cancel();
          this.#deactivate();
        }
      }
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
